import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import TipsAndUpdatesRoundedIcon from '@mui/icons-material/TipsAndUpdatesRounded';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form1099MISCInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form 1099 MISC instructions for Tax Year 2025"
          description="Learn how to fill out Form 1099-MISC for Tax Year 2025 with step-by-step instructions from TaxZerone to report miscellaneous payments accurately and on time."
          keywords=""
          canonical="resources/information-returns/form-1099-misc-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg">
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 5,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 1099 MISC</span>
          </span>{' '}
          Instructions
        </Typography>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-1099-misc-instructions/ "
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 1099-MISC at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form 1099-MISC
                        </strong>{' '}
                        is used to report miscellaneous payments such as rents,
                        royalties, prizes, medical payments, attorney payments,
                        and other non-wage income.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        Businesses must provide copies to recipients by{' '}
                        <strong style={{ fontWeight: 600 }}>January 31</strong>{' '}
                        and file the form with the IRS by{' '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>{' '}
                        (paper filing) or{' '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>{' '}
                        (electronic filing).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        Form 1099-MISC is generally required for reporting
                        payments that meet IRS thresholds, including{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $10 or more{' '}
                        </strong>
                        in royalties or substitute payments and{' '}
                        <strong style={{ fontWeight: 600 }}>
                          $600 or more{' '}
                        </strong>
                        in rents, prizes, awards, medical payments, attorney
                        fees, and other specified income payments.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        This article covers{' '}
                        <strong style={{ fontWeight: 600 }}>
                          line-by-line instructions,{' '}
                        </strong>
                        including payer information such as name, address, and
                        TIN/EIN, along with recipient details including name,
                        address, TIN, and account information in{' '}
                        <strong style={{ fontWeight: 600 }}>Box 1</strong> for
                        for rents,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 2</strong> for
                        royalties,{' '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong> for
                        other income such as prizes and awards, and{' '}
                        <strong style={{ fontWeight: 600 }}>Box 4</strong> for
                        federal income tax withheld. The article also covers{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 5-15</strong>,
                        which include fishing boat proceeds, medical and health
                        care payments, direct sales, crop insurance proceeds,
                        attorney payments, fish purchased for resale, deferred
                        compensation, and FATCA reporting requirements, along
                        with{' '}
                        <strong style={{ fontWeight: 600 }}>Boxes 16-18</strong>{' '}
                        for optional state reporting information.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        Penalties for late filing of Form 1099-MISC range from{' '}
                        <strong style={{ fontWeight: 600 }}>$60 to $680</strong>{' '}
                        per return, based on filing delay and intentional
                        disregard.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="6" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          File Form 8809
                        </Link>{' '}
                        to request an automatic filing extension.
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          File Form 15397
                        </Link>{' '}
                        to request the full{' '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension{' '}
                        </strong>{' '}
                        for furnishing recipient copies.
                      </span>
                    </li>
                  </Box>

                  {/* Publisher needs an Organization with a logo ImageObject — no meta tags, same pattern */}
                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 1099-MISC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  Form 1099-MISC is an annual tax return that businesses and
                  individuals must file with the IRS. It reports payments made
                  during the year to independent contractors or others who are
                  not employees. The form is used to report various types of
                  miscellaneous income paid to an individual or business, such
                  as:
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Rent payments</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Royalties</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Prizes and awards</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>Payments to attorneys</ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments for services to unincorporated businesses
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The main purpose of Form 1099-MISC is to inform recipients of
                  the income and the IRS of the payment so that taxes can be
                  assessed appropriately. With Form 1099-MISC, the IRS can
                  ensure that recipients claim the income on their tax returns
                  and pay any taxes owed.
                </Typography>
                <Alert
                  severity="info"
                  sx={{ mb: 4, borderLeft: '3px solid #0288d1' }}
                  icon={<TipsAndUpdatesRoundedIcon />}
                >
                  <b>Tip:</b> Make sure you collect W-9 forms from any
                  contractors you pay so you have the necessary information to
                  complete the form 1099-misc accurately.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 1099-MISC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  Businesses or individuals who made certain payments over
                  specified thresholds to independent contractors must file Form
                  1099-MISC. You must{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-MISC
                  </Link>{' '}
                  if you paid at least:
                </Typography>

                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      $10 or more in royalties or substitute payments in lieu of
                      dividends or tax-exempt interest
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      $600 in
                      <ul>
                        <li>Rent payments</li>
                        <li>Prizes or awards</li>
                        <li>Other income payments</li>
                        <li>
                          Cash paid from a notional principal contract to an
                          individual, partnership, or estate
                        </li>
                        <li>Fishing boat proceeds</li>
                        <li>Medical and health care payments</li>
                        <li>Crop insurance proceeds</li>
                        <li>Fees paid to an attorney</li>
                        <li>
                          Deferrals to nonqualified deferred compensation plans
                        </li>
                        <li>Section 409A deferred compensation.</li>
                      </ul>
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payments exempt from Form 1099-MISC:
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  You are generally not required to report the following
                  payments on Form 1099-MISC, even if they are taxable income to
                  the recipient:
                </Typography>

                <List sx={{ mb: 4 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments to a corporation (except certain payments to
                      attorneys and medical service providers)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments for merchandise, freight, storage, etc.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Rent payments to real estate agents or property managers
                      (but agents must issue 1099s to property owners)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Wages paid to employees (report on Form W-2 instead)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Certain military differential wage payments (report on
                      Form W-2)
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Business travel allowances paid to employees
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Cost of current life insurance protection (report on{' '}
                      <Link
                        href="/resources/information-returns/form-w-2-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        Form W-2
                      </Link>{' '}
                      or{' '}
                      <Link
                        href="/resources/information-returns/form-1099-r-instructions/"
                        sx={{ color: '#0202ff' }}
                      >
                        Form 1099-R
                      </Link>
                      )
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Payments to tax-exempt organizations like charities,
                      governments, HSAs, etc.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Homeowner assistance from HFA Hardest Hit Fund
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Certain disability or death benefits for public safety
                      officers
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Compensation for wrongful incarceration
                    </ListItemText>
                  </ListItem>
                </List>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  When is the Form 1099-MISC due date?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <Link
                    href="/resources/information-returns/form-1099-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    due date for filing Form 1099-MISC
                  </Link>{' '}
                  is March 31st of the year following the payment. For example,
                  if you made payments in 2025, you must file Form 1099-MISC by{' '}
                  <b>March 31, 2026</b>.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Avoid the last-minute 1099-MISC deadline stress!
                      </Typography>
                      <Typography variant="body2">
                        E-file now for easy IRS compliance and peace of mind.
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099miscinstructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography variant="body1" sx={{ mb: 4 }}>
                  In addition to filing Form 1099-MISC with the IRS, payers must
                  also provide copies of the form to recipients by February 02,
                  2026. Furnishing the 1099-MISC to recipients by this deadline
                  gives them the information they need to properly report the
                  income on their own tax returns.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-MISC penalty
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The IRS imposes penalties for failing to file Form 1099-MISC
                  by the deadline or failing to furnish copies to recipients on
                  time. The penalties are as follows:
                </Typography>

                <Box component="div" sx={{ mb: 2 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Days late
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Penalty per return
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>Up to 30 days</TableCell>
                          <TableCell>$60</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>31 days late through August 1 </TableCell>
                          <TableCell>$130</TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>After August 1 or not filed</TableCell>
                          <TableCell>$340</TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Intentional disregard</TableCell>
                          <TableCell>$680</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 5,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '24px !important',
                      }}
                    >
                      Don&apos;t risk penalties!
                    </Typography>
                    <Typography variant="body2">
                      Be proactive! E-file your 1099-MISC forms ahead of the
                      deadline.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099miscinstructions`}
                    >
                      E-File Now
                    </Button>
                  </Box>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 1099-MISC Instructions - How to fill out?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 1099-MISC.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Payer details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-payer-img.webp" />
                  <source srcSet="/assets/images/misc-payer-img.png" />
                  <img
                    src="/assets/images/misc-payer-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-payer details"
                    width={590}
                    height={348}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Enter your or your business name
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter your complete address
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter your TIN (SSN if you&apos;re an individual; EIN if
                    you&apos;re a business)
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Recipient details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-recipient-img.webp" />
                  <source srcSet="/assets/images/misc-recipient-img.png" />
                  <img
                    src="/assets/images/misc-recipient-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-recipient details"
                    width={590}
                    height={549}
                    className="proportional-img"
                  />
                </picture>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Recipient’s TIN:</span>{' '}
                    Enter the TIN (SSN for an individual; EIN for a business)
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the recipient’s name
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Enter the recipient’s complete address, including street
                    address, city or town, state or province, country, and ZIP
                    or foreign postal code
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <span style={{ fontWeight: 600 }}> Account number:</span> If
                    you have multiple accounts set up for a single recipient to
                    whom you will need to issue more than one Form 1099-MISC,
                    you must include the applicable account number for that
                    recipient on each form.
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> 2nd TIN not:</span> The
                    IRS requires you to verify that you have the correct
                    Taxpayer Identification Number (TIN) for individuals and
                    businesses for whom you{' '}
                    <Link
                      href="/information-returns/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      file 1099 forms
                    </Link>
                    .
                  </li>
                </ul>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Rents
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-1-img.webp" />
                  <source srcSet="/assets/images/misc-box-1-img.png" />
                  <img
                    src="/assets/images/misc-box-1-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box 1-rents"
                    width={244}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report rent payments of $600 or more in box 1, including:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Office space rentals. But don&apos;t report rents paid to
                    real estate agents or property managers. Agents must report
                    rents to property owners on their own 1099-MISC forms.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Machine and equipment rentals. If the rental includes the
                    machine and an operator, prorate the rent between the
                    machine (box 1) and the operator&apos;s fee (
                    <Link
                      href="/resources/information-returns/form-1099-nec-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-NEC
                    </Link>
                    ).
                  </li>
                  <li>
                    Land rentals, such as farmers paying for grazing rights.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Royalties
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-2-img.webp" />
                  <source srcSet="/assets/images/misc-box-2-img.png" />
                  <img
                    src="/assets/images/misc-box-2-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-2-royalties"
                    width={244}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report royalty payments of $10 or more in box 2. This includes
                  royalties for:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Oil, gas, and mineral rights: Report the gross amount before
                    taxes or other reductions.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Intangible property, such as patents, copyrights,
                    trademarks, and trade names.
                  </li>
                  <li>
                    Literary works: Report royalties paid by a publisher to an
                    author or their literary agent. The agent must report the
                    gross amount paid to the author.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  DO NOT include the following royalty types in box 2:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Surface royalties (report in box 1 instead).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Working interest royalties (report on{' '}
                    <Link
                      href="/resources/information-returns/form-1099-nec-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-NEC
                    </Link>{' '}
                    ).
                  </li>
                  <li>
                    Timber royalties under pay-as-cut contracts (report on{' '}
                    <Link
                      href="/resources/information-returns/form-1099-s-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 1099-S
                    </Link>
                    ).
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Other income
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-3-img.webp" />
                  <source srcSet="/assets/images/misc-box-3-img.png" />
                  <img
                    src="/assets/images/misc-box-3-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-3-other income"
                    width={244}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report any other income of $600 or more that doesn&apos;t fit
                  into other boxes, such as:
                </Typography>
                <ul style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Prizes and awards not for services provided. Report the fair
                    market value of prizes won on game shows.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Sweepstakes winnings not involving a wager. Report gambling
                    winnings on{' '}
                    <Link
                      href="/resources/information-returns/form-w-2g-instructions/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form W-2G
                    </Link>
                    .
                  </li>
                  <li>Any other miscellaneous income over $600.</li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-4-img.webp" />
                  <source srcSet="/assets/images/misc-box-4-img.png" />
                  <img
                    src="/assets/images/misc-box-4-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-4-federal income tax withheld"
                    width={244}
                    height={60}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report any federal income tax withheld from payments reported
                  in boxes 1, 2, 3, 5, 6, 8, 9, and 10. Withholding is required
                  if recipients have not provided their TIN.
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Also, report income tax withheld from payments to members of
                  Native American tribes for gaming activities.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Fishing boat proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-5-img.webp" />
                  <source srcSet="/assets/images/misc-box-5-img.png" />
                  <img
                    src="/assets/images/misc-box-5-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-5-fishing boat proceeds"
                    width={243}
                    height={117}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  If you operate a fishing boat, report crew members&apos; share
                  of proceeds from the catch in box 5. Specifically:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    For boats with fewer than 10 crew members, report each
                    individual&apos;s share of the proceeds or fair market value
                    of an in-kind distribution.
                  </li>
                  <li>
                    Also, report up to $100 per trip paid to crew members for
                    additional duties like mate, engineer, or cook.
                  </li>
                </ul>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 4, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> DO NOT include wages reported on Form W-2.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Medical and health care payments
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-6-img.webp" />
                  <source srcSet="/assets/images/misc-box-6-img.png" />
                  <img
                    src="/assets/images/misc-box-6-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-6-medical and health care payments"
                    width={244}
                    height={117}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report payments of $600 or more to medical service providers,
                  including:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Doctors, dentists, chiropractors, etc.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Hospitals, clinics, nursing homes, etc.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Other providers of health care services
                  </li>
                  <li>Health and accident insurers</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payments include fees for services, injections, drugs,
                  dentures, etc. Report total amounts paid, not just charges for
                  services.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payments to incorporated medical providers must be reported.
                  However, payments to pharmacies for prescription drugs do not
                  need to be reported.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Do not report payments made to tax-exempt hospitals,
                  government-owned hospitals, or government health care
                  providers.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Payer made direct sales totaling $5,000 or more
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-7-img.webp" />
                  <source srcSet="/assets/images/misc-box-7-img.png" />
                  <img
                    src="/assets/images/misc-box-7-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-7-payer made direct sales totaling $5,000 or more"
                    width={243}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Check the box if you paid a person $5,000 or more in
                  commissions for selling consumer products on a buy-sell,
                  deposit-commission, or other commission basis for resale
                  anywhere other than a permanent retail establishment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Do not enter a dollar amount — just check the box if this type
                  of payment over $5,000 was made.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You can report these direct sales commissions either in box 7
                  of Form 1099-MISC or box 2 of Form 1099-NEC.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> You must report these sales on only one form.
                </Alert>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Substitute payments in lieu of dividends or interest
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-8-img.webp" />
                  <source srcSet="/assets/images/misc-box-8-img.png" />
                  <img
                    src="/assets/images/misc-box-8-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-8-substitute payments in lieu of dividends or interest"
                    width={244}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  Report aggregate substitute payments of $10 or more received
                  on behalf of a customer for:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>Dividends</li>

                  <li>
                    Tax-exempt interest accrued while their securities were
                    loaned
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These payments are made to a broker when a customer&apos;s
                  securities are loaned out.{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    File 1099 misc form
                  </Link>{' '}
                  and furnish a copy to the customer for whom you received the
                  payment.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Substitute payments are made on behalf of individuals, trusts,
                  partnerships, companies, etc. They are not made on behalf of
                  tax-exempt organizations, governments, or foreign governments.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Crop insurance proceeds
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-9-img.webp" />
                  <source srcSet="/assets/images/misc-box-9-img.png" />
                  <img
                    src="/assets/images/misc-box-9-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-9-crop insurance proceeds"
                    width={243}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Report crop insurance proceeds of $600 or more paid to
                  farmers, unless the farmer elected to capitalize the expenses
                  under IRS section 278, 263A, or 447.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  In those cases, the insurance proceeds can be excluded.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Gross proceeds paid to an attorney
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-10-img.webp" />
                  <source srcSet="/assets/images/misc-box-10-img.png" />
                  <img
                    src="/assets/images/misc-box-10-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-10-gross proceeds paid to an attorney"
                    width={244}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Report gross proceeds of $600 or more paid to an attorney for
                  legal services, regardless of whether the attorney performed
                  services for you or another party.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: Fish purchased for resale
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-11-img.webp" />
                  <source srcSet="/assets/images/misc-box-11-img.png" />
                  <img
                    src="/assets/images/misc-box-11-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-11-fish purchased for resale"
                    width={243}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you purchase fish for resale, report total cash payments of
                  $600 or more paid to any person engaged in the trade or
                  business of catching fish.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  &ldquo;Fish&rdquo; includes all aquatic life.
                  &ldquo;Cash&rdquo; means coin, currency, cashier&apos;s
                  checks, etc. Not personal checks.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  You must keep records of each payment, but only report the
                  total annual amount on 1099-MISC.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: Section 409A deferrals
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-12-img.webp" />
                  <source srcSet="/assets/images/misc-box-12-img.png" />
                  <img
                    src="/assets/images/misc-box-12-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-12-section 409A deferrals"
                    width={244}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You are not required to complete this box.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you choose to report Section 409A deferrals, enter the
                  total amount of at least $600 deferred during the year under
                  nonqualified deferred compensation (NQDC) plans for
                  nonemployee recipients.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  The deferrals include earnings on current and prior year
                  deferrals. See IRS Notice 2008-115 for more information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: FATCA filing requirement checkbox
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-13-img.webp" />
                  <source srcSet="/assets/images/misc-box-13-img.png" />
                  <img
                    src="/assets/images/misc-box-13-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-13-fatca filing requirement checkbox"
                    width={124}
                    height={89}
                    className="proportional-img"
                  />
                </picture>

                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Check this box if you&apos;re a U.S. payer reporting on
                    Form(s) 1099, including payments on Form 1099-MISC, to
                    fulfill your obligation for a U.S. account under chapter 4
                    of the Internal Revenue Code (Regulations section
                    1.1471-4(d)(2)(iii)(A)).
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Also, check if you&apos;re an FFI reporting payments to a
                    U.S. account based on an election in Regulations section
                    1.1471-4(d)(5)(i)(A).
                  </li>
                  <li>
                    Check if you&apos;re an FFI making the election in
                    Regulations section 1.1471-4(d)(5)(i)(A) and reporting a
                    U.S. account for chapter 4 purposes, with either no
                    reportable payments on Form 1099 or payments below the
                    reporting threshold for any applicable Form 1099.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 15: Nonqualified deferred compensation
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-15-img.webp" />
                  <source srcSet="/assets/images/misc-box-15-img.png" />
                  <img
                    src="/assets/images/misc-box-15-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-15-nonqualified deferred compensation"
                    width={244}
                    height={89}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter amounts deferred (including earnings) that must be
                  included in income under section 409A due to plan
                  non-compliance.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  Exclude correctly reported amounts on prior-year Forms
                  1099-MISC, corrected Forms 1099-MISC, Form W-2, or Form W-2c.
                  Also, exclude amounts with substantial risk of forfeiture
                  under section 409A.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Boxes 16–18: State Information
                </Typography>
                <picture>
                  <source srcSet="/assets/images/misc-box-16-img.webp" />
                  <source srcSet="/assets/images/misc-box-16-img.png" />
                  <img
                    src="/assets/images/misc-box-16-img.png"
                    loading="lazy"
                    alt="Form 1099-misc-box-16-18-state informatio"
                    width={675}
                    height={88}
                    className="proportional-img"
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For payers in the Combined Federal/State Filing Program or
                  submitting paper copies, these optional boxes are for
                  reporting payments to two states.
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Box 16: Report any state income tax withheld from the
                    payment
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Box 17: Enter the 2-letter state abbreviation and
                    payer&apos;s ID number for that state
                  </li>
                  <li>
                    Box 18: Enter the amount of compensation associated with
                    that state
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to file Form 1099-MISC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file Form 1099-MISC
                  </Link>{' '}
                  either electronically or on paper, but electronic filing is
                  recommended by the IRS.
                </Typography>

                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Form 1099-MISC electronic filing (E-filing)
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  E-filing is a fast, simple way to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    efile Form 1099-MISC
                  </Link>
                  . You can use an IRS-authorized service like TaxZerone to
                  electronically submit your forms. The benefits of e-filing
                  include:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    Quick process: It only takes a few minutes to complete and
                    file the forms online.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Instant IRS notifications: You receive a confirmation when
                    the IRS processes your returns.
                  </li>
                  <li>
                    Furnish recipient copies easily: You can electronically
                    deliver copies to recipients with the help of an e-file
                    service provider.
                  </li>
                </ul>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 22, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Paper filing
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  If you choose to paper-file Form 1099-MISC, here are the steps
                  you need to follow:
                </Typography>
                <ol style={{ marginBottom: 32 }}>
                  <li style={{ marginBottom: 16 }}>
                    Download and print blank Form 1099-MISC from IRS.gov.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Manually complete the forms with recipient and business
                    information.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Mail the forms to the IRS{' '}
                    <Link
                      href="#1099-misc-mailing-address"
                      sx={{ color: '#0202ff' }}
                    >
                      address listed
                    </Link>{' '}
                    in the instructions.
                  </li>
                  <li>Mail copies to recipients by January 31.</li>
                </ol>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 4, mb: 4, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> * The due date is March 31 if filed
                  electronically.
                </Alert>
                <Box
                  component="div"
                  sx={{
                    backgroundColor: '#caf0f8',
                    borderLeft: '5px solid #0077b6',
                    p: 1,
                    mb: 4,
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{ mb: 1, fontWeight: 600, display: 'flex' }}
                  >
                    <ReportProblemRoundedIcon
                      sx={{ mr: 1, color: 'rgb(237, 108, 2)' }}
                    />{' '}
                    Important notes for paper filing:
                  </Typography>
                  <ul style={{ marginBottom: 0 }}>
                    <li style={{ marginBottom: 16 }}>
                      Mail forms several weeks early to ensure timely delivery.
                    </li>
                    <li style={{ marginBottom: 16 }}>
                      You must also furnish paper copies to recipients.
                    </li>
                    <li id="1099-misc-mailing-address">
                      Printing, mailing, and tracking forms takes more time,
                      effort, and cost than e-filing. Alternatively, you can
                      <Link
                        href="/information-returns/efile-irs-form-1099-misc/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        e-file 1099-MISC forms
                      </Link>{' '}
                      for faster processing and less cost.
                    </li>
                  </ul>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Where to send Form 1099-MISC - Mailing address
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  The mailing address for Form 1099-MISC depends on your
                  business location. Below is a table that summarizes the
                  mailing address for form 1099 misc:
                </Typography>
                <Box component="div" sx={{ mb: 5 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell sx={{ color: '#444', width: 300 }}>
                            If your business operates in or your legal residence
                            is…
                          </TableCell>

                          <TableCell sx={{ color: '#444', width: 300 }}>
                            Mail Form 1099-MISC to…
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell>
                            Alabama, Arizona, Arkansas, Delaware, Florida,
                            Georgia, Kentucky, Maine, Massachusetts,
                            Mississippi, New Hampshire, New Jersey, New Mexico,
                            New York, North Carolina, Ohio, Texas, Vermont,
                            Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury,
                            <br />
                            Internal Revenue Service Center,
                            <br />
                            Austin, TX 73301.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>
                            Alaska, Colorado, Hawaii, Idaho, Illinois, Indiana,
                            Iowa, Kansas, Michigan, Minnesota, Missouri,
                            Montana, Nebraska, Nevada, North Dakota, Oklahoma,
                            Oregon, South Carolina, South Dakota, Tennessee,
                            Utah, Washington, Wisconsin, Wyoming
                          </TableCell>
                          <TableCell>
                            Department of the Treasury,
                            <br />
                            Internal Revenue Service Center,
                            <br />
                            P.O. Box 219256,
                            <br />
                            Kansas City, MO 64121-9256.
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell>
                            California, Connecticut, District of Columbia,
                            Louisiana, Maryland, Pennsylvania, Rhode Island,
                            West Virginia
                          </TableCell>
                          <TableCell>
                            Department of the Treasury,
                            <br />
                            Internal Revenue Service Center,
                            <br /> Ogden, UT 84201
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell>Outside the United States</TableCell>
                          <TableCell>
                            Department of the Treasury,
                            <br />
                            Internal Revenue Service Center,
                            <br />
                            Austin, TX 73301.
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  How to e-file Form 1099-MISC?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can complete Form 1099-MISC filing in 3 simple steps.
                  E-filing your 1099-MISCs is an easy process if you&apos;re
                  prepared with the necessary information.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Information required to file Form 1099-MISC:
                </Typography>
                <ul style={{ marginBottom: 24 }}>
                  <li style={{ marginBottom: 16 }}>
                    Payer information, such as name, address, and TIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Recipient information, such as name, address, and TIN.
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    Miscellaneous payments made
                  </li>
                  <li>Federal income tax withheld, if any</li>
                </ul>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Once you have this information ready, you can e-file your
                  1099-MISCs in just 3 steps using TaxZerone:
                </Typography>
                <ul style={{ marginBottom: 40 }}>
                  <li style={{ marginBottom: 16 }}>
                    <b>Step 1:</b> Fill out Form 1099-MISC
                  </li>
                  <li style={{ marginBottom: 16 }}>
                    <b>Step 2:</b> Review & transmit the return
                  </li>
                  <li>
                    <b>Step 3:</b> Send the recipient copy
                  </li>
                </ul>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  E-file Form 1099-MISC with TaxZerone
                </Typography>

                <Typography variant="body1" sx={{ mb: 0 }}>
                  TaxZerone offers several advantages for{' '}
                  <Link
                    href="/information-returns/efile-irs-form-1099-misc/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    e-filing your Form 1099-MISC
                  </Link>
                  :
                </Typography>
                <List sx={{ mb: 0 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        IRS form validation:
                      </span>{' '}
                      TaxZerone checks your returns for errors before filing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Bulk uploading:</span>{' '}
                      Easily file high volumes by batch uploading recipient
                      data.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Email delivery:</span>{' '}
                      Send recipient copies digitally via email.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> ZeroneVault:</span>{' '}
                      Share copies through ZeroneVault, a secure online access
                      portal, for anytime, anywhere access.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Low cost:</span>{' '}
                      Complete filing at the best price in the industry—as low
                      as <span style={{ fontWeight: 600 }}>$0.59 per form</span>
                      .
                    </ListItemText>
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Get these benefits by e-filing your
              <br />
              Form 1099-MISC with TaxZerone
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=1099miscinstructions`}
              >
                E-file Form 1099-MISC
              </Button>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Effortless, efficient, and affordable e-filing awaits you.
              </Typography>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form1099MISCInstructions;
